// SEO Configuration for João Xavier Portfolio
export const seoConfig = {
  // Site-wide defaults
  siteUrl: 'https://joaoxavier.netlify.app', // Update with actual domain
  siteName: 'João Xavier - Classical Pianist',
  defaultTitle: 'João Xavier - Portuguese Classical Pianist',
  defaultDescription: 'Official portfolio of João Xavier, one of Portugal\'s most promising classical pianists. Discover performances, repertoire, and concert information.',
  defaultKeywords: 'João Xavier, classical pianist, piano, Portuguese pianist, classical music, concert pianist, piano recital, classical performance',
  author: 'João Xavier',
  social: {
    youtube: 'https://www.youtube.com/@JMMPXavier',
    email: 'joaommxavier@gmail.com'
  },
  
  // Default OpenGraph image (should be hosted on your domain)
  defaultImage: '/images/João_Xavier_HomePage.webP',
  
  // Page-specific configurations
  pages: {
    home: {
      title: 'João Xavier - Portuguese Classical Pianist | Official Portfolio',
      description: 'Welcome to the official portfolio of João Xavier, one of Portugal\'s most promising classical pianists. Discover performances, upcoming concerts, and musical journey.',
      keywords: 'João Xavier, classical pianist, Portuguese pianist, piano recital, classical music, concert pianist, home',
      path: '/'
    },
    about: {
      title: 'About João Xavier - Biography & Musical Journey | Classical Pianist',
      description: 'Learn about João Xavier\'s musical journey from Portugal to international stages. Graduate of Moscow State Tchaikovsky Conservatory and acclaimed competition winner.',
      keywords: 'João Xavier biography, classical pianist background, Moscow Conservatory, Portuguese musician, piano education, musical journey',
      path: '/about'
    },
    media: {
      title: 'João Xavier Performances - Videos & Recordings | Classical Piano',
      description: 'Watch João Xavier\'s piano performances and recordings. Experience classical piano masterpieces performed by one of Portugal\'s finest pianists.',
      keywords: 'João Xavier videos, classical piano recordings, piano performances, YouTube, classical music videos, piano recital recordings',
      path: '/media'
    },
    gallery: {
      title: 'Photo Gallery - João Xavier in Concert | Classical Piano Images',
      description: 'Explore photos of João Xavier performing in prestigious concert halls across Europe. See moments from performances and recitals.',
      keywords: 'João Xavier photos, classical pianist images, concert photos, piano recital pictures, performance gallery, concert hall',
      path: '/gallery'
    },
    repertoire: {
      title: 'Piano Repertoire - João Xavier | Classical Music Programs',
      description: 'Discover João Xavier\'s extensive piano repertoire featuring works by Bach, Mozart, Chopin, Liszt, and contemporary composers.',
      keywords: 'classical piano repertoire, Bach, Mozart, Chopin, Liszt, piano music, classical compositions, concert programs',
      path: '/repertoire'
    },
    contact: {
      title: 'Contact João Xavier - Booking & Inquiries | Classical Pianist',
      description: 'Get in touch with João Xavier for concert bookings, masterclasses, and collaboration opportunities. Professional classical pianist available worldwide.',
      keywords: 'João Xavier contact, piano booking, classical pianist hire, concert booking, masterclass booking, piano lessons',
      path: '/contact'
    }
  }
};

// Helper function to get page-specific SEO data
export const getPageSEO = (pageKey) => {
  const pageData = seoConfig.pages[pageKey];
  if (!pageData) {
    return {
      title: seoConfig.defaultTitle,
      description: seoConfig.defaultDescription,
      keywords: seoConfig.defaultKeywords,
      url: seoConfig.siteUrl,
      image: `${seoConfig.siteUrl}${seoConfig.defaultImage}`
    };
  }

  return {
    title: pageData.title,
    description: pageData.description,
    keywords: pageData.keywords,
    url: `${seoConfig.siteUrl}${pageData.path}`,
    image: `${seoConfig.siteUrl}${seoConfig.defaultImage}`,
    path: pageData.path
  };
};

// Structured Data for João Xavier (JSON-LD)
export const getPersonStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "João Xavier",
    "jobTitle": "Classical Pianist",
    "description": "Portuguese classical pianist and graduate of Moscow State Tchaikovsky Conservatory",
    "url": seoConfig.siteUrl,
    "email": seoConfig.social.email,
    "image": `${seoConfig.siteUrl}${seoConfig.defaultImage}`,
    "sameAs": [
      seoConfig.social.youtube
    ],
    "nationality": "Portuguese",
    "knowsLanguage": ["Portuguese", "English", "Russian"],
    "alumniOf": [
      {
        "@type": "CollegeOrUniversity",
        "name": "Moscow State Tchaikovsky Conservatory",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "Russia"
        }
      },
      {
        "@type": "CollegeOrUniversity", 
        "name": "Koninklijk Conservatorium Brussel",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "Belgium"
        }
      }
    ],
    "performsAs": {
      "@type": "MusicGroup",
      "name": "João Xavier"
    },
    "genre": "Classical Music",
    "instrument": "Piano",
    "award": [
      "Competition Winner",
      "Classical Music Awards"
    ]
  };
};

// Website structured data
export const getWebsiteStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": seoConfig.siteName,
    "url": seoConfig.siteUrl,
    "description": seoConfig.defaultDescription,
    "author": {
      "@type": "Person",
      "name": seoConfig.author
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${seoConfig.siteUrl}/?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
};

// Breadcrumb structured data
export const getBreadcrumbStructuredData = (currentPath) => {
  const breadcrumbMap = {
    '/': { name: 'Home', position: 1 },
    '/about': { name: 'About', position: 2 },
    '/media': { name: 'Media', position: 2 },
    '/gallery': { name: 'Gallery', position: 2 },
    '/repertoire': { name: 'Repertoire', position: 2 },
    '/contact': { name: 'Contact', position: 2 }
  };

  const breadcrumbs = [];
  
  // Always include home
  breadcrumbs.push({
    "@type": "ListItem",
    "position": 1,
    "name": "Home",
    "item": seoConfig.siteUrl
  });

  // Add current page if not home
  if (currentPath !== '/' && breadcrumbMap[currentPath]) {
    breadcrumbs.push({
      "@type": "ListItem", 
      "position": breadcrumbMap[currentPath].position,
      "name": breadcrumbMap[currentPath].name,
      "item": `${seoConfig.siteUrl}${currentPath}`
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs
  };
};